import React, { FC, useState, useEffect } from 'react';
import Button from '@fv-components/button';

import { getConfig } from '../../config';
import { getIsOutlook } from '../../util-helpers/office';
import { safeRedirect, setAddInStorage } from '../../util-helpers/common';
import EphemeralId from '../../EphemeralId';
import { openDialog } from '../../util-helpers/officeDialog';
import { ENV_SETTNGS, generateHeaders, getEnvironmentSuffix } from '../FVID';
import { version } from '../../version.json';

const css = require('./Landing.module.scss');

const Landing: FC = () => {
  const [isLoginComplete, setIsLoginComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [updating, setUpdating] = useState(false);
  const [landingNotification, setLandingNotification] = useState<string | null>(null);

  const { hostUrl } = getConfig();

  const processLoginProperties = (args: any) => {
    setAddInStorage(args);
    generateHeaders(true).then(() => {
      setIsLoginComplete(true);
    });
  };

  const refreshAddin = () => {
    setUpdating(true);
    try {
      const timestamp = new Date().getTime();
      const url = new URL(window.location.href);
      url.searchParams.set('t', timestamp.toString());

      window.setTimeout(() => {
        window.location.href = url.toString();
      }, 1000);
    } catch (error) {
      console.error('Failed to refresh:', error);
      window.location.reload();
    }
  };

  useEffect(() => {
    if (isLoginComplete) {
      safeRedirect(hostUrl);
    }
  }, [isLoginComplete, hostUrl]);

  const autoOpenLoginDialog = (environment: string) => {
    setErrorMessage('');
    openDialog({
      url: `${ENV_SETTNGS[getEnvironmentSuffix()].CLIENT_ROOT}/login/${environment}`,
      onMessage: processLoginProperties,
      onError: (error: any) => {
        displayErrorMessage(error);
      }
    });
  };

  useEffect(() => {
    if (window.location.pathname === "/signout") {
      window.location.assign("/logout");
      return;
    }

    const autoSelect = localStorage.getItem('autoSelectEnvironment') === 'true';
    const lastEnvironment = localStorage.getItem('lastEnvironment');

    if (autoSelect && lastEnvironment) {
      localStorage.removeItem('autoSelectEnvironment');

      setLandingNotification('Automatically opening login dialog to refresh your session...');

      const loginEnvironment = lastEnvironment === 'CA' ? 'ca' : 'us';
      autoOpenLoginDialog(loginEnvironment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayErrorMessage = (message: any) => {
    setErrorMessage(typeof message === 'string' ? message : JSON.stringify(message, null, 2));
  };

  const isOutlook = getIsOutlook();

  const defaultMsgTitle = 'Select your location to sign in';
  const defaultMsgDesc = isOutlook ? 'The Filevine Add-in provides a window into your Filevine projects from inside Outlook. Quickly send emails to your Filevine project, add activities, and reference project data, all without leaving Outlook.'
    : 'The Filevine Add-in provides a window into your Filevine projects from inside Office. Quickly save documents, spreadsheets, or presentations to your Filevine project, add activities, and reference project data, all without leaving Office.';
  const ctaMessage = defaultMsgTitle;
  // eslint-disable-next-line no-nested-ternary
  const ctaDescription = defaultMsgDesc;

  // Auto-dismiss notification after 5 seconds
  useEffect(() => {
    if (landingNotification) {
      const timer = setTimeout(() => {
        setLandingNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [landingNotification]);

  return <>
    {landingNotification && (
      <div className={css.notification}>
        <div className={css.text}>{landingNotification}</div>
      </div>
    )}
    <div className={css.loginContainer}>
      <EphemeralId />
      <img src="assets/fv-logo.png" alt="Filevine" />
      <span className={css.loginCTA}>
        {ctaMessage}
      </span>
      <Button
        id="login_button_fvid_us"
        raised
        type="submit"
        className={css.loginButton}
        data-test="sign-in"
        onClick={() => {
          setErrorMessage('');
          openDialog({
            url: `${ENV_SETTNGS[getEnvironmentSuffix()].CLIENT_ROOT}/login/US`,
            onMessage: processLoginProperties,
            onError: (error: any) => {
              displayErrorMessage(error);
            }
          });
        }}>
        United States
      </Button>
      <Button
        id="login_button_fvid_ca"
        raised
        type="submit"
        className={css.loginButton}
        data-test="sign-in"
        onClick={() => {
          setErrorMessage('');
          openDialog({
            url: `${ENV_SETTNGS[getEnvironmentSuffix()].CLIENT_ROOT}/login/CA`,
            onMessage: processLoginProperties,
            onError: (error: any) => {
              displayErrorMessage(error);
            }
          });
        }}>
        Canada
      </Button>
      <small>
        <p>
          {ctaDescription}
        </p>
        <br />
        <br />
        <div>
          Don’t have a Filevine account?
        </div>
        <a href="https://www.filevine.com/demo-request?utm_source=outlook" target="_blank" rel="noopener noreferrer">Schedule your demo today.</a>
      </small>
      <div className={css.version}>
        {`v${version}`}
      </div>
      <Button
        onClick={refreshAddin}
        disabled={updating}
        className={css.refreshButton}
        title="Refresh Add-in"
      >
        <span>Refresh Add-in</span>
      </Button>
      {errorMessage && <div>
        {`An error occurred: ${errorMessage}`}
      </div>}
    </div>
  </>;
};

export default Landing;
